import { api, ApiError } from './client'
import type { Game, MatchEvent, PLTableEntry } from '../types'
import type { TeamId } from '../teams'

/**
 * The client's view of our own football API, following the per-domain wrapper
 * convention of `leagues.ts` / `me.ts` / `rivals.ts`.
 *
 * The client never talks to a provider. Everything here hits our server, which serves it from our
 * database — that fan-in is what makes 500 users watching a live match cost what 1 user costs.
 */

export type FixturesByGameweek = Record<number, Game[]>
export type ResultsByGameweek = Record<number, Record<string, { homeScore: number; awayScore: number }>>

export interface TeamMeta {
  id: TeamId
  name: string
  shortName: string | null
  abbreviation: string
  isActive: boolean
}

/** The eight canonical statuses from server/football/types.ts, as the client sees them. */
export type FixtureStatus =
  | 'scheduled'
  | 'live'
  | 'halftime'
  | 'finished'
  | 'postponed'
  | 'cancelled'
  | 'abandoned'
  | 'unknown'

export interface LiveFixture {
  id: string
  status: FixtureStatus
  minute: number | null
  homeScore: number | null
  awayScore: number | null
}

export interface LiveSnapshot {
  serverTime: string
  gameweek: number | null
  fixtures: LiveFixture[]
  updatedAt: string | null
  stale: boolean
}

/** Thrown when the server has no football data yet — a cold start, not a client bug. */
export class FootballDataUnavailableError extends Error {
  constructor() {
    super('Football data has not synced yet.')
    this.name = 'FootballDataUnavailableError'
  }
}

function rethrowUnavailable(error: unknown): never {
  if (error instanceof ApiError && error.status === 503) throw new FootballDataUnavailableError()
  throw error
}

export async function getFixtures(): Promise<FixturesByGameweek> {
  try {
    return await api.get<FixturesByGameweek>('/fixtures')
  } catch (error) {
    rethrowUnavailable(error)
  }
}

export async function getResults(): Promise<ResultsByGameweek> {
  try {
    return await api.get<ResultsByGameweek>('/results')
  } catch (error) {
    rethrowUnavailable(error)
  }
}

export async function getStandings(): Promise<PLTableEntry[]> {
  try {
    return await api.get<PLTableEntry[]>('/standings')
  } catch (error) {
    rethrowUnavailable(error)
  }
}

export async function getTeams(): Promise<TeamMeta[]> {
  return api.get<TeamMeta[]>('/teams')
}

export interface FixtureTeamStats {
  teamId: TeamId
  shotsTotal: number | null
  shotsOnGoal: number | null
  corners: number | null
  goalkeeperSaves: number | null
  yellowCards: number | null
  redCards: number | null
  possessionPercent: number | null
  fouls: number | null
}

export interface FixtureDetail {
  fixtureId: string
  status: LiveFixture['status']
  /**
   * True for anything not `finished` — the timeline is still being written. It is
   * what separates "no events yet" from "no event details for this match", which is the whole
   * reason the route can serve an in-play fixture at all.
   */
  partial: boolean
  /** Already shaped by the server's `toMatchEvents`, home/away resolved. */
  timeline: MatchEvent[]
  /** Empty until the post-match pass: live per-team statistics are deliberately out of scope. */
  teams: FixtureTeamStats[]
  halfTime: { home: number | null; away: number | null }
  fullTime: { home: number | null; away: number | null }
}

export interface FixtureDetailResponse {
  detail: FixtureDetail | null
  etag: string | null
}

/**
 * One fixture's events. Sends `If-None-Match` and answers `detail: null` on a
 * `304`, so an expanded card riding the live poll costs a header exchange while nothing happens.
 *
 * Like `getLive`, this uses `fetch` directly rather than `api.get`: the shared client throws on any
 * non-2xx, and a 304 is the success case here.
 */
export async function getFixtureDetail(
  fixtureId: string,
  etag?: string | null
): Promise<FixtureDetailResponse> {
  const res = await fetch(`/api/fixtures/${encodeURIComponent(fixtureId)}/detail`, {
    credentials: 'include',
    headers: etag ? { 'If-None-Match': etag } : {},
  })
  if (res.status === 304) return { detail: null, etag: etag ?? null }
  if (res.status === 503) throw new FootballDataUnavailableError()
  if (!res.ok) {
    throw new ApiError(res.status, undefined, `API GET /fixtures/${fixtureId}/detail failed with ${res.status}`)
  }
  return { detail: (await res.json()) as FixtureDetail, etag: res.headers.get('etag') }
}

/**
 * One gameweek's goals and sendings-off, keyed by fixture id. A fixture with
 * neither is absent, so a quiet gameweek is `{}`.
 */
export type GameweekEventsByFixture = Record<
  string,
  { redCards: { home: number; away: number }; goals: MatchEvent[] }
>

export interface GameweekEventsResponse {
  events: GameweekEventsByFixture | null
  etag: string | null
}

/**
 * Every fixture's goals and sendings-off for one gameweek, so the fixture list can show scorers and
 * mark the side that went down to ten without a request per row. `null` on a `304`.
 */
export async function getGameweekEvents(
  gameweek: number,
  etag?: string | null
): Promise<GameweekEventsResponse> {
  const res = await fetch(`/api/fixtures/gameweek-events?gameweek=${gameweek}`, {
    credentials: 'include',
    headers: etag ? { 'If-None-Match': etag } : {},
  })
  if (res.status === 304) return { events: null, etag: etag ?? null }
  if (res.status === 503) throw new FootballDataUnavailableError()
  if (!res.ok) {
    throw new ApiError(res.status, undefined, `API GET /fixtures/gameweek-events failed with ${res.status}`)
  }
  return { events: (await res.json()) as GameweekEventsByFixture, etag: res.headers.get('etag') }
}

/**
 * A fixture's pre-match win/draw/away split. Three integers that always total
 * exactly 100 — the normalising happens once, server-side, on write.
 */
export interface MatchForecast {
  home: number
  draw: number
  away: number
}

/** One gameweek's forecasts, keyed by fixture id. A fixture with no model is simply absent. */
export type ForecastsByFixture = Record<string, MatchForecast>

export interface GameweekForecastsResponse {
  forecasts: ForecastsByFixture | null
  etag: string | null
}

/**
 * Every scheduled fixture's forecast for one gameweek, in one request — the picker cycles through
 * the whole gameweek, so opening a fixture must fire nothing. `null` on a `304`.
 *
 * The server serves this for `scheduled` fixtures only, so a gameweek that has kicked off comes
 * back progressively emptier and finally `{}`.
 */
export async function getGameweekForecasts(
  gameweek: number,
  etag?: string | null
): Promise<GameweekForecastsResponse> {
  const res = await fetch(`/api/fixtures/forecasts?gameweek=${gameweek}`, {
    credentials: 'include',
    headers: etag ? { 'If-None-Match': etag } : {},
  })
  if (res.status === 304) return { forecasts: null, etag: etag ?? null }
  if (res.status === 503) throw new FootballDataUnavailableError()
  if (!res.ok) {
    throw new ApiError(res.status, undefined, `API GET /fixtures/forecasts failed with ${res.status}`)
  }
  return {
    forecasts: (await res.json()) as ForecastsByFixture,
    etag: res.headers.get('etag'),
  }
}

export interface LiveResponse {
  snapshot: LiveSnapshot | null
  etag: string | null
}

/**
 * The polling endpoint. Sends `If-None-Match`, and returns `snapshot: null` on a `304` — an idle
 * poll then costs almost nothing, which is what makes a 10s client interval reasonable.
 *
 * Deliberately uses `fetch` directly rather than `api.get`: the shared client throws on any
 * non-2xx, and a 304 is the *success* case here.
 */
export async function getLive(etag?: string | null): Promise<LiveResponse> {
  const res = await fetch('/api/live', {
    credentials: 'include',
    headers: etag ? { 'If-None-Match': etag } : {},
  })
  if (res.status === 304) return { snapshot: null, etag: etag ?? null }
  if (res.status === 503) throw new FootballDataUnavailableError()
  if (!res.ok) throw new ApiError(res.status, undefined, `API GET /live failed with ${res.status}`)
  return { snapshot: (await res.json()) as LiveSnapshot, etag: res.headers.get('etag') }
}
